import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #pendingVariantId;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.variantChanged.bind(this));
    this.init();
  }

  init() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get("variant");

    if (!variantId) return;

    this.updateVariantDescription(parseInt(variantId));

    this.dispatchEvent(
      new VariantSelectedEvent({ id: variantId })
    );

    /** @type {HTMLInputElement | null} */
    const selectedOption= this.querySelector(
      "fieldset.variant-option--swatches input:checked"
    );
    if (!selectedOption) return;

    const variantMediaId = selectedOption.dataset.optionMediaId;

    setTimeout(() => {
      this.updateVariantMedia(variantMediaId);
    }, 1000);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? "" })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Extract variant ID from the event target before fetching
    let variantId;
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === "radio"
    ) {
      variantId = event.target.dataset.variantId
        ? parseInt(event.target.dataset.variantId, 10)
        : null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId
        ? parseInt(selectedOption.dataset.variantId, 10)
        : null;
    }

    // Store variant ID to use after fetch completes (only if not null)
    this.#pendingVariantId = variantId || undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(event.target),
      loadsNewProduct
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", String(variantId));
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, "", url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    const option1Id = selectedOption.dataset.option1Id;
    const option2Id = selectedOption.dataset.option2Id;

    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (option1Id && option2Id && !source) {
      params.push(`option_values=${option1Id},${option2Id}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        "&"
      )}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[data-variant-helper="${this.#pendingVariantId}"]`
        )?.textContent;

        let variantData = null;
        if (textContent) {
          try {
            variantData = JSON.parse(textContent);
          } catch (e) {
            console.error("Failed to parse variant JSON:", e);
          }
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.updatePriceFromResponse(html);
          this.updateAddToCartButton(variantData, html);
          this.updateVariantSKUFromResponse(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId && variantData) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              })
            );
          }
        }

        const variantId = this.#pendingVariantId || variantData?.id;

        this.#pendingVariantId = undefined; // Clear it after use

        if (variantId) {
          this.updateVariantDescription(variantId);
          this.updateVariantMediaFromResponse(html);
          const DatavarId = this.closest(".product-details")
            ?.querySelector("product-form-component")
            ?.querySelector('input[name="id"]');

          if (DatavarId) {
            DatavarId.setAttribute("value", String(variantId));
          }

          const stickyATCHeader = document.querySelector("sticky-atc-header");

          if (stickyATCHeader) {
            const stickyATCVarId =
              stickyATCHeader.querySelector('input[name="id"]');

            if (stickyATCVarId) {
              stickyATCVarId.setAttribute("value", String(variantId));
            }

            const gatedVariantIds = (
              stickyATCHeader.dataset.gatedVariants || ""
            )
              .split(",")
              .filter(Boolean);
            const isGated = gatedVariantIds.includes(String(variantId));

            stickyATCHeader.dataset.variantGated = isGated ? "true" : "false";

            if (isGated) {
              stickyATCHeader.classList.remove("is-visible");
            }

            const stickyAddButton =
              stickyATCHeader.querySelector('button[name="add"]');

            if (stickyAddButton) {
              if (isGated) {
                stickyAddButton.setAttribute("disabled", "disabled");
                stickyAddButton.dataset.gatedDisabled = "true";
              } else if (stickyAddButton.dataset.gatedDisabled === "true") {
                stickyAddButton.removeAttribute("disabled");
                delete stickyAddButton.dataset.gatedDisabled;
              }
            }
          }
        } else {
          console.warn("No variant ID found to fetch metafields", {
            pendingVariantId: this.#pendingVariantId,
            variantData,
            selectedOption: this.selectedOption,
          });
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Updates the variant SKU from the response.
   * @param {Document} newHtml - The new HTML document.
   */
  updateVariantSKUFromResponse(newHtml) {
    const newVariantSKU = newHtml.querySelector('[data-sku]')?.textContent;
    if (!newVariantSKU) {
      return;
    }

    const currentVariantSKU = document.querySelector('[data-sku]');
    if (currentVariantSKU) {
      currentVariantSKU.innerHTML = newVariantSKU;
    }
  }
  /**
   * Updates the product price from the response.
   * @param {Document} newHtml - The new HTML document.
   */
  updatePriceFromResponse(newHtml) {
    const newPriceContainer = newHtml.querySelector(
      'product-price [ref="priceContainer"]'
    );
    
    if (!newPriceContainer) return;
  
    const productId = this.dataset.productId;
    const priceElements = document.querySelectorAll("product-price");
  
    // Extract new price data once
    const newPriceData = {
      price: /** @type {HTMLElement} */(newPriceContainer.querySelector(".price")),
      comparePrice: /** @type {HTMLElement} */(newPriceContainer.querySelector(".compare-at-price")),
      unitPrice: /** @type {HTMLElement} */(newPriceContainer.querySelector(".unit-price"))
    };
  
    /**
     * Updates the price container.
     * @param {HTMLElement} container - The container element.
     * @param {Object} newData - The new data.
     * @param {HTMLElement} newData.price - The new price.
     * @param {HTMLElement} newData.comparePrice - The new compare price.
     * @param {HTMLElement} newData.unitPrice - The new unit price.
     */
    const updatePriceContainer = (container, newData) => {
      if (!container) return;
  
      // Update main price
      const currentPrice = container.querySelector(".price");
      if (currentPrice && newData.price && 
          currentPrice.innerHTML !== newData.price.innerHTML) {
        currentPrice.innerHTML = newData.price.innerHTML;
      }
  
      // Update compare-at-price
      const currentCompare = container.querySelector(".compare-at-price");
      if (currentCompare && newData.comparePrice) {
        if (currentCompare.innerHTML !== newData.comparePrice.innerHTML) {
          currentCompare.innerHTML = newData.comparePrice.innerHTML;
        }
      } else if (currentCompare && !newData.comparePrice) {
        currentCompare.remove();
      } else if (!currentCompare && newData.comparePrice) {
        const parent = currentPrice?.parentElement;
        if (parent) {
          parent.insertBefore(
            newData.comparePrice.cloneNode(true),
            parent.firstChild
          );
        }
      }
  
      // Update unit price
      const currentUnit = container.querySelector(".unit-price");
      if (currentUnit && newData.unitPrice) {
        if (currentUnit.innerHTML !== newData.unitPrice.innerHTML) {
          morph(currentUnit, newData.unitPrice);
        }
      } else if (!currentUnit && newData.unitPrice) {
        container.appendChild(newData.unitPrice.cloneNode(true));
      } else if (currentUnit && !newData.unitPrice) {
        currentUnit.remove();
      }
    };
  
    // Update all product-price elements
    priceElements.forEach((priceElement) => {
      if (!(priceElement instanceof HTMLElement)) return;
  
      if (productId && priceElement.dataset.productId && 
          priceElement.dataset.productId !== productId) {
        return;
      }
  
      const container = /** @type {HTMLElement} */(priceElement.querySelector('[ref="priceContainer"]'));
      updatePriceContainer(container, newPriceData);
    });
  
    // Update sticky header price
    const stickyHeader = document.querySelector("sticky-atc-header");
    if (stickyHeader) {
      const stickyContainer = /** @type {HTMLElement} */(stickyHeader.querySelector(
        '.sticky-atc-header__price [ref="priceContainer"]'
      ));
      updatePriceContainer(stickyContainer, newPriceData);
    }
  }

  /**
   * Updates the variant media from the response.
   * @param {Document} newHtml - The new HTML document.
   */
  updateVariantMediaFromResponse(newHtml) {
    const newSelectedOption = newHtml.querySelector(
      "fieldset.variant-option--swatches input:checked"
    );
    const variantMediaId = newSelectedOption
      ? /** @type {HTMLImageElement} */(newSelectedOption).dataset.optionMediaId
      : undefined;

    if (this.updateVariantMedia(variantMediaId)) return;

    // No swatch thumbnail to click (text/pill variant options, or variant images sourced from
    // a metafield with no native variant-image link) - replace the whole gallery instead.
    this.replaceMediaGallery(newHtml);
  }

  /**
   * Updates the variant media by clicking its matching thumbnail.
   * @param {number | string | undefined} variantMediaId - The variant media ID.
   * @returns {boolean} True if a matching thumbnail was found and clicked.
   */
  updateVariantMedia(variantMediaId) {
    if (!variantMediaId) return false;

    const mediaGallery = document.querySelector(
      ".slideshow-controls__thumbnails-container"
    );
    if (!mediaGallery) return false;

    /** @type {HTMLElement | null} */
    const mediaElement = mediaGallery.querySelector(
      `[data-media-id="${variantMediaId}"]`
    );
    if (!mediaElement) return false;

    (mediaElement).click();
    return true;
  }

  /**
   * Replaces the current media gallery with the one from the fetched response.
   * @param {Document} newHtml - The new HTML document.
   */
  replaceMediaGallery(newHtml) {
    const newMediaGallery = newHtml.querySelector("media-gallery");
    const currentMediaGallery = document.querySelector("media-gallery");
    if (!newMediaGallery || !currentMediaGallery) return;

    currentMediaGallery.replaceWith(newMediaGallery);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Fetches variant metafields from Shopify API.
   * @param {number} variantId - The variant ID.
   * @deprecated This method is deprecated and will be removed in the future.
   */
  async fetchVariantMetafields(variantId) {
    if (!variantId) {
      console.warn("No variant ID provided to fetchVariantMetafields");
      return;
    }

    const productUrl = this.dataset.productUrl;
    if (!productUrl) {
      console.warn("No product URL found");
      return;
    }

    try {
      const url = `${productUrl}?variant=${variantId}&section_id=variant-metafields`;

      const response = await fetch(url);

      if (!response.ok) {
        console.error(
          "Failed to fetch variant metafields:",
          response.status,
          response.statusText
        );
        const text = await response.text();
        console.error("Response body:", text.substring(0, 500));
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const metafieldsScript = doc.querySelector(
        'script[type="application/json"]'
      );

      if (metafieldsScript) {
        const metafields = JSON.parse(metafieldsScript.textContent);
        this.updateVariantDescriptionFromMetafields(metafields);
      } else {
        console.warn(
          'No script tag with type="application/json" found in response'
        );
      }
    } catch (error) {
      console.error("Error fetching variant metafields:", error);
    }
  }

  /**
   * Updates the variant description from the data-variant-helper attribute.
   * @param {number} variantId - The variant ID.
   */
  updateVariantDescription(variantId) {
    const dataScript = this.querySelector(`[data-variant-helper="${variantId}"]`);
    if (!dataScript) return;

    const data = JSON.parse(dataScript.textContent);
    const { description, details } = data;

    this.updateDescriptionField(description);
    this.updateDetailsField(details);
  }

  /**
   * Updates the description field.
   * @param {string} descriptionHTML - The description string from rich text metafield.
   */
  updateDescriptionField(descriptionHTML) {
    if (!descriptionHTML) return;

    /** @type {HTMLElement | null } */
    const descriptionElement = document.querySelector(
      "[data-product-description]"
    );
    if (!descriptionElement) return;

    descriptionElement.innerHTML = descriptionHTML;
  }

  /**
   * Updates the details field.
   * Hides the details container if the details string is empty or null.
   * @param {string} detailsHTML - The details string from rich text metafield.
   */
  updateDetailsField(detailsHTML) {
    /** @type {HTMLElement | null } */
    const detailsContainer = document.querySelector(".detail-class");
    if (!detailsContainer) return;

    if (!detailsHTML || detailsHTML.trim() === "") {
      detailsContainer.style.display = "none";
      return;
    }

    /** @type {HTMLElement | null } */
    const detailsElement = detailsContainer.querySelector(".details-content");
    if (!detailsElement) return;

    detailsElement.innerHTML = detailsHTML;
    detailsContainer.style.display = "block";
  }


  /**
   * Updates variant description from metafields object.
   * @param {Object} metafields - The metafields object.
   * @deprecated This method is deprecated and will be removed in the future.
   */
  updateVariantDescriptionFromMetafields(metafields) {
    /** @type {any} */
    const meta = metafields;
    const variantDescription = meta?.custom?.variant_description;
    const details = meta?.custom?.details;

    // Find the description element
    const descriptionElement = document.querySelector(
      "[data-product-description]"
    );

    if (descriptionElement && variantDescription) {
      // Handle rich text metafield - if it's an object with value property containing HTML
      let descriptionHtml = "";

      if (typeof variantDescription === "string") {
        descriptionHtml = variantDescription;
      } else if (variantDescription?.value) {
        // Rich text metafield structure
        if (typeof variantDescription.value === "string") {
          descriptionHtml = variantDescription.value;
        } else if (
          variantDescription.value?.type === "root" ||
          variantDescription.value?.children
        ) {
          // Rich text JSON structure - convert to HTML
          descriptionHtml = this.#convertRichTextToHtml(
            variantDescription.value
          );
        }
      } else if (variantDescription?.html) {
        descriptionHtml = variantDescription.html;
      }

      if (descriptionHtml) {
        descriptionElement.innerHTML = descriptionHtml;
      }
    }
    // Handle details metafield - update .variant-details-element .metafield-rich_text_field
    if (details) {
      const detailsContainer = document.querySelector(".detail-class");
      const detailsElement =
        detailsContainer?.querySelector(".details-content");

      if (detailsElement) {
        let detailsHtml = "";
        if (typeof details === "string") {
          detailsHtml = details;
        } else if (details?.value) {
          if (typeof details.value === "string") {
            detailsHtml = details.value;
          } else if (
            details.value?.type === "root" ||
            details.value?.children
          ) {
            detailsHtml = this.#convertRichTextToHtml(details.value);
          }
        }
        if (detailsHtml) {
          detailsElement.innerHTML = detailsHtml;
        }
      } else {
        console.warn(
          "Details element not found: .variant-details-element .metafield-rich_text_field"
        );
      }
    }
  }

  /**
   * Converts Shopify rich text metafield JSON structure to HTML string.
   * @param {any} node - The rich text node object.
   * @returns {string} The HTML string.
   */
  #convertRichTextToHtml(node) {
    if (!node || typeof node !== "object") return "";

    // Text nodes
    if (node.type === "text" && node.value) {
      // Convert newlines to <br> and handle HTML entities
      return node.value.replace(/\n/g, "<br>");
    }

    // If it's an array (children), process each child
    if (Array.isArray(node)) {
      return node
        .map((/** @type {any} */ child) => this.#convertRichTextToHtml(child))
        .join("");
    }

    // Process children if they exist
    const children = node.children || [];
    const content = children
      .map((/** @type {any} */ child) => this.#convertRichTextToHtml(child))
      .join("");

    // Handle different node types
    switch (node.type) {
      case "root":
        return content;
      case "paragraph":
        return `<p>${content}</p>`;
      case "heading":
        const level = node.level || 1;
        return `<h${level}>${content}</h${level}>`;
      case "list":
        const listTag = node.listType === "ordered" ? "ol" : "ul";
        return `<${listTag}>${content}</${listTag}>`;
      case "list-item":
        return `<li>${content}</li>`;
      case "link":
        return `<a href="${node.url || "#"}">${content}</a>`;
      case "bold":
        return `<strong>${content}</strong>`;
      case "italic":
        return `<em>${content}</em>`;
      default:
        return content;
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked"
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  /**
   * Updates the add-to-cart button based on variant availability.
   * @param {any} variantData - The variant data object.
   * @param {Document} [responseHtml] - The response HTML document.
   */
  updateAddToCartButton(variantData, responseHtml) {
    console.log("updateAddToCartButton", variantData);
    if (!variantData) return;

    // Find the add-to-cart-component - it's typically in the same product form or nearby
    const productForm = this.closest(".product-details")?.querySelector(
      "product-form-component"
    );
    const addToCartComponent = productForm?.querySelector(
      "add-to-cart-component"
    );

    // If not found in product form, try finding it in the same section
    const addToCartComponentFallback = this.closest(
      ".shopify-section"
    )?.querySelector("add-to-cart-component");
    const addToCart = /** @type {any} */ (
      addToCartComponent || addToCartComponentFallback
    );

    if (!addToCart) return;

    const { isAvailable } = variantData;
    const button = addToCart.refs?.addToCartButton;
    const textElement = button?.querySelector(".add-to-cart-text__content");

    if (!button) return;
    // Update disabled state
    if (isAvailable) {
      button.removeAttribute("disabled");
    } else {
      button.setAttribute("disabled", "true");
    }

    // Update text content - get from response HTML if available, otherwise use defaults
    if (textElement) {
      if (isAvailable) {
        // Try to get the text from the response HTML
        let addToCartText = "Add to cart";
        if (responseHtml) {
          const responseTextElement = responseHtml.querySelector(
            ".add-to-cart-text__content"
          );
          if (
            responseTextElement &&
            !responseTextElement.textContent?.includes("Sold out") &&
            !responseTextElement.textContent?.includes("Out of Stock")
          ) {
            addToCartText = responseTextElement.textContent.trim();
          }
        }
        textElement.textContent = addToCartText;
      } else {
        // Set sold out text - get from response HTML if available
        let soldOutText = "Sold out";
        if (responseHtml) {
          const responseTextElement = responseHtml.querySelector(
            ".add-to-cart-text__content"
          );
          if (
            responseTextElement &&
            (responseTextElement.textContent?.includes("Sold out") ||
              responseTextElement.textContent?.includes("Out of Stock"))
          ) {
            soldOutText = responseTextElement.textContent.trim();
          }
        }
        textElement.textContent = soldOutText;
      }
    }
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
